import { getEnvironmentFeatureFlags } from './environmentFeatureFlags';
import { isFeatureFlagEnabled } from './mockFeatureFlags';
import {
  FEATURE_FLAG_BRAND,
  type FeatureFlagEvaluationContext,
  type FeatureFlagEvaluationResult,
  type FeatureFlagHandle,
} from './types';

/**
 * Development-only: a misdefined flag denies access in production exactly as a
 * closed one does, and the console is the one place that difference can be
 * seen without shipping the detail to users.
 */
const diagnostic = (message: string) => {
  if (import.meta.env.DEV) {
    console.error(`[feature-flags] ${message}`);
  }
};

/** Guards the boundary against a plain object shaped like a handle; see FEATURE_FLAG_BRAND. */
const isFeatureFlagHandle = (value: unknown): value is FeatureFlagHandle =>
  typeof value === 'object' && value !== null && FEATURE_FLAG_BRAND in value;

/**
 * Every required flag plus its ancestors, parents first, each appearing once.
 * `seen` also stops a cyclic chain, which `defineFeatureFlags` has already
 * flagged as a problem.
 */
const withAncestors = (requiredFlags: readonly FeatureFlagHandle[]): FeatureFlagHandle[] => {
  const expanded: FeatureFlagHandle[] = [];
  const seen = new Set<FeatureFlagHandle>();

  requiredFlags.forEach((flag) => {
    const chain: FeatureFlagHandle[] = [];

    for (let current: FeatureFlagHandle | undefined = flag; current && !seen.has(current); current = current.parent) {
      seen.add(current);
      chain.push(current);
    }

    expanded.push(...chain.reverse());
  });

  return expanded;
};

/**
 * The single place access is decided. Every gate in the app — route guards,
 * navigation, Authz permission filtering, individual buttons — comes through
 * here, so the ordering below is the ordering the whole product obeys.
 *
 * The order is not an optimisation, it is the contract: a deployment-wide
 * switch is checked first, then whether the platform is even running the
 * service, and only then whether this user is allowed. Asking Authz about a
 * feature the deployment does not have would leak its existence and put load on
 * a service for nothing.
 *
 * A flag counts as open only when the environment's `featureFlags` map holds
 * `true` for its id. The server lists every declared flag explicitly — false
 * included — so an operator reading the response sees each flag's exact name
 * next to its state. An omitted id and a missing map still deny access,
 * because the alternative — treating unknown as on — means every mistake
 * (a typo'd id, an older server that never heard of the flag, a response that
 * failed to parse) ships the feature rather than hiding it.
 */
export const evaluateFeatureFlags = (
  requiredFlags: readonly FeatureFlagHandle[],
  context: FeatureFlagEvaluationContext
): FeatureFlagEvaluationResult => {
  // Nothing declared means nothing to gate. Most of the app is not behind a
  // flag, and this keeps generic call sites from special-casing that.
  if (requiredFlags.length === 0) return { status: 'allowed' };

  if (!requiredFlags.every(isFeatureFlagHandle)) {
    diagnostic('Unknown feature flag handle. Access denied.');
    return { status: 'blocked', stage: 'flags', reason: 'unknown' };
  }

  // Parents are cumulative: a child cannot be open while its parent is closed,
  // so `workflowsActionsEnabled` is checked against `workflowsEnabled` too. Expanding
  // here means no call site has to remember to name the parent as well.
  const flags = withAncestors(requiredFlags);

  const malformed = flags.find((flag) => flag.problem)?.problem;
  if (malformed) {
    diagnostic(malformed.message);
    return { status: 'blocked', stage: 'flags', reason: malformed.reason, flagId: malformed.flagId };
  }

  const env = context.env;
  if (!env) return { status: 'loading', stage: 'flags' };

  const environmentFlags = getEnvironmentFeatureFlags(env) ?? {};
  const disabled = flags.find((flag) => !isFeatureFlagEnabled(flag.id, environmentFlags));
  if (disabled) {
    return { status: 'blocked', stage: 'flags', reason: 'disabled', flagId: disabled.id };
  }

  // Discovery answers "is the backing service actually deployed here", which a
  // flag alone cannot: an operator can leave a flag on in an environment where
  // the service was never installed. Flags with no endpoint of their own — child
  // capabilities, UI-only switches — skip this stage entirely.
  const flagsNeedingDiscovery = flags.filter((flag) => flag.discoveryKeys.length > 0);
  if (flagsNeedingDiscovery.length === 0) return { status: 'allowed' };

  // Read here, and one context member at a time: a `const { env, discovery } =
  // context` at the top would touch the discovery getter before the flags have
  // had their say, which is the one thing this ordering exists to prevent.
  const discovery = context.discovery;
  if (!discovery) return { status: 'loading', stage: 'discovery' };

  for (const flag of flagsNeedingDiscovery) {
    const missingDiscoveryKeys = flag.discoveryKeys.filter((key) => !discovery[key]);
    if (missingDiscoveryKeys.length > 0) {
      return { status: 'blocked', stage: 'discovery', reason: 'unavailable', flagId: flag.id, missingDiscoveryKeys };
    }
  }

  return { status: 'allowed' };
};
